import "./page404.css";

import { Button } from "reactstrap";
import { InlineSpinner } from "./InlineSpinner";
import cloverfieldLogo from "../static/img/cloverfield_icon_base.png";
import { getApiFailures } from "../services/apiErrors";
import { getBreadcrumbs } from "../services/breadcrumbs";
import { sendErrorReport } from "../services/backendRequests";
import { useSelector } from "react-redux";
import { useState } from "react";

// Opaque, UTF-8-safe Base64 of the diagnostic payload: keeps user data out of
// clear text in the report. Not encryption — Hiphen support decodes it
// (e.g. `atob(...)` / any Base64 decoder) to read the full context.
const encodeDiagnostics = (payload) =>
  btoa(
    encodeURIComponent(JSON.stringify(payload)).replace(
      /%([0-9A-F]{2})/g,
      (_, hex) => String.fromCharCode(parseInt(hex, 16))
    )
  );

// Store shape without values: array→length, scalar→type, object→keys down to
// slice contents (2 levels), then key count. Shows sizes, leaks no user data.
const shapeOf = (value, depth = 0) => {
  if (Array.isArray(value)) return `array(${value.length})`;
  if (value === null) return "null";
  if (typeof value !== "object") return typeof value;
  if (depth >= 2) return `object(${Object.keys(value).length})`;
  return Object.fromEntries(
    Object.entries(value).map(([key, val]) => [key, shapeOf(val, depth + 1)])
  );
};

export const CrashPage = ({ error }) => {
  const fullState = useSelector((state) => state);
  const user = useSelector((state) => state.user);
  const self = user.self;
  const trial = useSelector((state) => state.resultMap.trial);
  const selectedContract = useSelector(
    (state) => state.resultMap.selectedContract
  );
  // The ErrorBoundary sits above the Router, so read the browser location.
  const pathname = window.location.pathname;

  // campaign & mission have no dedicated store slot, their id only lives in the
  // route, so we read them from the pathname.
  const routeId = (pattern) => pathname.match(pattern)?.[1] ?? null;

  // Captured once, at crash time: the user's local time (with timezone), the
  // matching France (Europe/Paris) time, plus UTC and the resolved timezone.
  const [datetime] = useState(() => {
    const now = new Date();
    return {
      user: now.toLocaleString(undefined, { timeZoneName: "short" }),
      france: now.toLocaleString("fr-FR", {
        timeZone: "Europe/Paris",
        timeZoneName: "short",
      }),
      utc: now.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  });

  const loc = window.location;
  const nav = window.navigator;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;

  const contractId = trial?.contract_id ?? selectedContract?.id ?? null;
  const contractName =
    (user.contracts ?? []).find((c) => c.id === contractId)?.name ??
    selectedContract?.name ??
    null;

  // Who, when, where — ids paired with their name when the store holds it.
  // campaign/mission: no store slice, id only. No email/phone.
  const context = {
    userId: self?.id ?? null,
    name: self?.fullname ?? null,
    companyId: self?.company?.id ?? null,
    companyName: self?.company?.name ?? null,
    backoffice: self?.backoffice ?? false,
    datetimeUser: datetime.user,
    datetimeFrance: datetime.france,
    datetimeUtc: datetime.utc,
    timezone: datetime.timezone,
    url: loc.href,
    pathname: loc.pathname,
    search: loc.search || null,
    hash: loc.hash || null,
    referrer: document.referrer || null,
    // trial & contract come straight from the redux store
    trialId: trial?.id ?? null,
    trialName: trial?.display_name ?? trial?.name ?? null,
    contractId,
    contractName,
    // campaign & mission are only in the route (no name available)
    campaignId: routeId(/^\/campaigns\/([^/]+)/),
    missionId: routeId(/^\/monitoring\/([^/]+)/),
  };

  // orphanTrials: trials whose site isn't in any contract — the map mismatch
  // that crashes here (same join as TrialBadge.jsx).
  const trials = user.trials ?? [];
  const siteIds = new Set(
    (user.contracts ?? []).flatMap((c) => c.sites?.map((s) => s.id) ?? [])
  );
  const data = {
    store: shapeOf(fullState),
    orphanTrials: trials.filter((t) => !siteIds.has(t.id)).map((t) => t.id),
    breadcrumbs: getBreadcrumbs(),
    apiFailures: getApiFailures(),
  };

  // Flag the common "logged-in user with no company" cause first in the report,
  // followed by the actual error, so support spots it at a glance once decoded.
  const noCompany = Boolean(self?.id) && !self.company;
  const err = error?.error;
  const report = encodeDiagnostics({
    ...(noCompany ? { reason: "No company affiliated" } : {}),
    error: {
      name: err?.name ?? null,
      message: err?.message ?? String(err ?? ""),
      stack: err?.stack ?? null,
      cause: err?.cause != null ? String(err.cause) : null,
    },
    componentStack: error?.errorInfo?.componentStack ?? null,
    context,
    data,
    environment: {
      userAgent: nav.userAgent,
      language: nav.language,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      connection: conn
        ? {
            effectiveType: conn.effectiveType ?? null,
            downlink: conn.downlink ?? null,
            rtt: conn.rtt ?? null,
            saveData: conn.saveData ?? null,
          }
        : null,
      appPlatform: process.env.REACT_APP_PLATFORM ?? null,
      nodeEnv: process.env.NODE_ENV ?? null,
    },
  });

  // "idle" | "sending" | "sent" | "error". Once "sent", the button is gone for
  // good so the report can't be submitted twice.
  const [status, setStatus] = useState("idle");

  // Sent on demand (button click) — no mailto, the mail is delivered to Hiphen
  // support straight from the backend.
  const sendReport = () => {
    setStatus("sending");
    const body = [
      "An unexpected error occurred in Cloverfield and a user sent this report.",
      "",
      "Encoded diagnostics below (Base64) — keep it as-is, the team decodes it:",
      "",
      report,
    ].join("\n");
    sendErrorReport({ subject: "Automatic error report", body }, user)
      .then(() => setStatus("sent"))
      .catch(() => setStatus("error"));
  };

  return (
    <div className="page-404">
      <div className="crash-card">
        <img
          className="cloverfield-logo"
          src={cloverfieldLogo}
          alt="cloverfield-logo"
        />
        <h1 className="crash-card__title">
          <svg
            className="crash-card__icon me-2"
            viewBox="-2 -4 68 68"
            width="1.8em"
            height="1.8em"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ verticalAlign: "-0.45em" }}
            aria-hidden="true"
          >
            {/* petals around the flower head */}
            <circle cx="32" cy="4" r="4.5" />
            <circle cx="42.6" cy="7.9" r="4.5" />
            <circle cx="46.5" cy="18" r="4.5" />
            <circle cx="42.6" cy="28.1" r="4.5" />
            <circle cx="21.4" cy="28.1" r="4.5" />
            <circle cx="17.5" cy="18" r="4.5" />
            <circle cx="21.4" cy="7.9" r="4.5" />
            {/* flower face */}
            <circle cx="32" cy="18" r="10" />
            {/* dead X eyes */}
            <line x1="25.5" y1="13.5" x2="29.5" y2="17.5" />
            <line x1="29.5" y1="13.5" x2="25.5" y2="17.5" />
            <line x1="34.5" y1="13.5" x2="38.5" y2="17.5" />
            <line x1="38.5" y1="13.5" x2="34.5" y2="17.5" />
            {/* sad mouth */}
            <path d="M28 24 Q32 20.5 36 24" />
            {/* drooping stem with a leaf */}
            <path d="M32 28 C 32 36 33 42 32 50" />
            <path d="M32 40 c -8 -3 -11 3 -2 5 c 4 -1 5 -4 2 -5 Z" />
            {/* pot rim */}
            <path d="M22 50 L42 50 L40 55 L24 55 Z" />
            {/* pot body */}
            <path d="M24.5 55 L27 62 L37 62 L39.5 55" />
          </svg>
          Something unexpected happened
        </h1>
        <p className="crash-card__text">
          {status === "sent"
            ? "The error report has been sent to Hiphen support. You can head back home."
            : "Sorry, an unexpected error occurred. Send the report to Hiphen support so we can look into it."}
        </p>

        {status === "sent" ? (
          <p className="text-success mb-0">
            <i className="fa-solid fa-circle-check me-2" />
            Report sent
          </p>
        ) : (
          <Button
            color="success"
            onClick={sendReport}
            disabled={status === "sending"}
          >
            {status === "sending" ? (
              <InlineSpinner className="me-2" />
            ) : (
              <i className="fa-solid fa-paper-plane me-2" />
            )}
            {status === "sending"
              ? "Sending…"
              : status === "error"
              ? "Retry sending report"
              : "Send report"}
          </Button>
        )}

        {status === "error" && (
          <p className="text-danger mb-0">
            Couldn&apos;t send the report automatically. Please retry.
          </p>
        )}

        <Button color="link" className="crash-card__home" href="/">
          Bring me back home
        </Button>
      </div>
    </div>
  );
};
